import { useAuth0 } from '@auth0/auth0-react';
import { resetUser } from '../analytics/mixpanel';
import { clearAccessibleCookies } from '../auth/clearCookies';
import { Button } from "./ui/button";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { User, LogOut, Settings } from "lucide-react";

interface UserProfileProps {
  onNavigate?: (view: "profile" | "settings") => void;
  variant?: "default" | "headerbar";
}

export function UserProfile({ onNavigate, variant = "default" }: UserProfileProps) {
  const { user, logout, isAuthenticated } = useAuth0();

  if (!isAuthenticated || !user) {
    return null;
  }

  const userInitials = user.name
    ? user.name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
    : user.email
    ? user.email[0].toUpperCase()
    : 'U';

  const handleLogout = () => {
    resetUser();
    try { clearAccessibleCookies(); } catch { /* Logout must still proceed if cookie access is blocked. */ }
    logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    });
  };

  const handleProfileClick = () => {
    if (onNavigate) {
      onNavigate("profile");
    }
  };

  const handleSettingsClick = () => {
    if (onNavigate) {
      onNavigate("settings");
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={`relative h-8 w-8 rounded-full ${variant === "headerbar" ? "headerbar__user-trigger" : ""}`}
          aria-label="Open user menu"
        >
          <Avatar className={`h-8 w-8 ${variant === "headerbar" ? "headerbar__user-avatar" : ""}`}>
            {variant === "default" && <AvatarImage src={user.picture} alt={user.name || user.email} />}
            <AvatarFallback className={variant === "headerbar" ? "headerbar__user-fallback" : ""}>{userInitials}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">
              {user.name || 'User'}
            </p>
            <p className="text-xs leading-none text-muted-foreground">
              {user.email}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleProfileClick}>
          <User className="mr-2 h-4 w-4" />
          <span>Profile</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleSettingsClick}>
          <Settings className="mr-2 h-4 w-4" />
          <span>Settings</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout}>
          <LogOut className="mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
